/**
 * Quality evidence for one piece (score card, independent QA, update locks, sources ledger) and for the workspace (histograms).
 *
 * Server contract (built in parallel; every field and route degrades to an empty state when it is missing):
 *   content item  scoreCard {<check>: {score: 0..3, reason}}  the 10-item rubric, graded by the independent reviewer
 *                 gateResult.awaitingQa                        the checks passed; independent QA has not finished yet
 *                 sourceContext.qa_review {passed, scores, findings[{claim, source, verdict, note}], unverified[]}
 *                 sourceContext.update {wpPostId, baseModifiedGmt, baseSlug, baseDisclaimerCount}  marks an UPDATE of a live post
 *                 sourceContext.ledger                         the sources ledger (text); the HTML comment in the body is the fallback
 *   GET /api/workspaces/:id/quality-histogram?days=30  ->  {checks: {<name>: {0, 1, 2, 3, saturated}}, items}
 *
 * Pure helpers first (no React, no network), the query hook at the bottom.
 */
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { gateOf, getOrNull, isNeedsFix } from "./agentic";
import { humanizeCheckName, isPublished, isRejected, toDate, toLines, truncate, type StudioItem } from "./helpers";

type Ctx = Record<string, any>;

function ctxOf(item: StudioItem): Ctx | null {
  const c = item.sourceContext as Ctx | null | undefined;
  return c && typeof c === "object" ? c : null;
}

function str(v: unknown): string | null {
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}

// ── The rubric ──────────────────────────────────────────────────────────────

export interface RubricCheck {
  id: string;
  label: string;
  /** One plain sentence on what a 3 means. */
  help: string;
}

/** The ten content-rubric checks, in the order the handoff lists them. Each is graded 0 to 3, not pass / fail. */
export const RUBRIC: RubricCheck[] = [
  { id: "factual_currency", label: "Facts are current", help: "Every figure was checked against a primary source." },
  { id: "enactment_status", label: "Law status stated correctly", help: "Says whether a rule is proposed, passed or in force, and says it correctly." },
  { id: "claim_to_source_binding", label: "Claims match their sources", help: "A sampled claim was checked against the source it cites." },
  { id: "gov_source_grounding", label: "Government sources", help: "At least three different government sites back the facts, and each one loads." },
  { id: "server_rendered_substance", label: "Substance is in the page", help: "The content is in the page's own HTML. AI crawlers do not run scripts." },
  { id: "practitioner_judgment_delta", label: "Practitioner judgment added", help: "What a practitioner adds beyond restating the CRA." },
  { id: "entity_specificity", label: "Named statutes, forms, rates", help: "Names the statutes, forms, agencies and rates involved." },
  { id: "single_topic_focus", label: "One topic", help: "The piece stays on a single topic." },
  { id: "reviewed_by_provenance", label: "Reviewed-by line", help: "The reviewed-by line is there and its date is not older than the last substantive edit." },
  { id: "answer_extractability", label: "Answer up front", help: "The direct answer is in the first 50 words." },
];

const RUBRIC_BY_ID = new Map(RUBRIC.map((r) => [r.id, r]));

export function rubricLabel(id: string): string {
  return RUBRIC_BY_ID.get(id)?.label ?? humanizeCheckName(id);
}

// ── Score card ──────────────────────────────────────────────────────────────

export type Score = 0 | 1 | 2 | 3;

/** Who graded a check: the automatic checks (gate), the independent QA review, the live-page check after publishing, or nobody yet. */
export type ScoreBy = "gate" | "qa" | "verify" | "pending";

export interface ScoreRow {
  id: string;
  label: string;
  help: string;
  /** null = not graded yet. */
  score: Score | null;
  reason: string | null;
  by: ScoreBy | null;
}

export const SCORE_BY_LABEL: Record<ScoreBy, string> = {
  gate: "the automatic checks",
  qa: "independent QA",
  verify: "the live-page check",
  pending: "nobody yet",
};

export interface ScoreCardView {
  rows: ScoreRow[];
  /** At least one check has a grade. */
  present: boolean;
  scored: number;
  /** Sum of the grades given so far, and the most they could add up to (3 per graded check). */
  sum: number;
  max: number;
  /** Graded 1 or lower, and graded 0. */
  weak: number;
  zeros: number;
}

export function toScore(x: unknown): Score | null {
  const n = typeof x === "number" ? x : typeof x === "string" && x.trim() !== "" ? Number(x) : NaN;
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(3, Math.round(n))) as Score;
}

interface RawEntry {
  score: Score | null;
  reason: string | null;
  by: ScoreBy | null;
}

function byOf(v: unknown): ScoreBy | null {
  return v === "gate" || v === "qa" || v === "verify" || v === "pending" ? v : null;
}

function entryOf(v: unknown): RawEntry {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const o = v as Ctx;
    return { score: toScore(o.score ?? o.value ?? o.grade), reason: str(o.reason ?? o.note ?? o.rationale ?? o.detail), by: byOf(o.by) };
  }
  return { score: toScore(v), reason: null, by: null };
}

/** `{check: {score, reason}}`, `{check: 2}` or `[{check, score, reason}]` become one map. */
function scoreMapOf(raw: unknown): Map<string, RawEntry> {
  const out = new Map<string, RawEntry>();
  if (Array.isArray(raw)) {
    for (const e of raw) {
      if (!e || typeof e !== "object") continue;
      const id = str((e as Ctx).check ?? (e as Ctx).name ?? (e as Ctx).id);
      if (id) out.set(id, entryOf(e));
    }
  } else if (raw && typeof raw === "object") {
    for (const [id, v] of Object.entries(raw as Ctx)) out.set(id, entryOf(v));
  }
  return out;
}

/** The score card: `item.scoreCard`, else the scores inside `sourceContext.qa_review`. Always all ten rubric rows; unknown extra checks follow. */
export function scoreCardOf(item: StudioItem): ScoreCardView {
  const ctx = ctxOf(item);
  let map = scoreMapOf((item as any).scoreCard);
  if (map.size === 0) map = scoreMapOf(ctx?.qa_review?.scores);

  const rows: ScoreRow[] = RUBRIC.map((r) => ({ ...r, ...(map.get(r.id) ?? { score: null, reason: null, by: null }) }));
  for (const [id, e] of Array.from(map.entries())) if (!RUBRIC_BY_ID.has(id)) rows.push({ id, label: humanizeCheckName(id), help: "", ...e });

  const graded = rows.filter((r) => r.score !== null);
  return {
    rows,
    present: map.size > 0 && graded.length > 0,
    scored: graded.length,
    sum: graded.reduce((n, r) => n + (r.score ?? 0), 0),
    max: graded.length * 3,
    weak: graded.filter((r) => (r.score ?? 3) <= 1).length,
    zeros: graded.filter((r) => r.score === 0).length,
  };
}

// ── Independent QA ──────────────────────────────────────────────────────────

/** The checks passed and the independent QA run has not finished (the server says so; this is never guessed). */
export function awaitingQaOf(item: StudioItem): boolean {
  if (isPublished(item) || isRejected(item) || isNeedsFix(item)) return false;
  const g = gateOf(item);
  return !!g && g.awaitingQa && g.passed !== false;
}

export type FindingTone = "ok" | "warn" | "bad" | "neutral";

/** Bad words first: "unsupported" contains "supported", "unverified" contains "verified", "not confirmed" contains "confirmed". */
export function verdictTone(verdict: string): FindingTone {
  const v = verdict.toLowerCase();
  if (/contradict|incorrect|inaccurate|wrong|false|mismatch|not match|unsupported|refut|fail|stale|outdated|error/.test(v)) return "bad";
  if (/unverif|not verif|not confirm|unconfirm|unclear|uncertain|unknown|partial|weak|could not|couldn't|cannot|can't|dispute|discrepan|caution|needs/.test(v)) return "warn";
  if (/verif|confirm|support|correct|accurate|match|pass|\bok\b|good|valid|true|consistent|agree/.test(v)) return "ok";
  return "neutral";
}

export interface QaFinding {
  claim: string;
  /** What the reviewer says the claim rests on: an address or a plain description. */
  source: string | null;
  sourceHref: string | null;
  verdict: string;
  tone: FindingTone;
  note: string | null;
}

export interface QaReviewView {
  /** null = a record exists but carries no verdict. */
  passed: boolean | null;
  findings: QaFinding[];
  /** Claims the reviewer could not check. */
  unverified: string[];
  counts: Record<FindingTone, number>;
  /** Who filed it and when, when the server says. */
  by: string | null;
  at: string | null;
  /** The draft changed after this review was filed (its content fingerprint is not the one the checks last ran on). */
  stale: boolean;
}

function hrefOf(s: string | null): string | null {
  if (!s) return null;
  const m = /https?:\/\/[^\s)>\]"']+/i.exec(s);
  return m ? m[0].replace(/[.,;]+$/, "") : null;
}

/** "canada.ca/en/revenue-agency/services/tax" shortened for a table cell. */
export function shortUrl(u: string): string {
  try {
    const x = new URL(u);
    return truncate(`${x.hostname.replace(/^www\./, "")}${x.pathname.replace(/\/+$/, "")}`, 56);
  } catch {
    return truncate(u, 56);
  }
}

function findingOf(raw: unknown): QaFinding | null {
  // The server accepts a bare string as a finding as well as an object.
  if (typeof raw === "string") {
    const s = raw.trim();
    return s ? { claim: s, source: null, sourceHref: hrefOf(s), verdict: "no verdict", tone: "neutral", note: null } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Ctx;
  const srcRaw = o.source ?? o.sourceUrl ?? o.url;
  const source = typeof srcRaw === "object" && srcRaw ? str((srcRaw as Ctx).url ?? (srcRaw as Ctx).href ?? (srcRaw as Ctx).label ?? (srcRaw as Ctx).name) : str(srcRaw);
  let verdict = str(o.verdict ?? o.status ?? o.result ?? o.outcome);
  if (verdict === null && typeof (o.verdict ?? o.passed ?? o.ok) === "boolean") verdict = (o.verdict ?? o.passed ?? o.ok) ? "confirmed" : "not confirmed";
  return {
    claim: str(o.claim ?? o.text ?? o.statement ?? o.fact) ?? "(claim not recorded)",
    source,
    sourceHref: hrefOf(source),
    verdict: verdict ?? "no verdict",
    tone: verdict ? verdictTone(verdict) : "neutral",
    note: str(o.note ?? o.notes ?? o.reason ?? o.detail ?? o.comment),
  };
}

/** `sourceContext.qa_review`. Null when the item has none (independent QA has not run, or never will for this piece). */
export function qaReviewOf(item: StudioItem): QaReviewView | null {
  const raw = ctxOf(item)?.qa_review;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Ctx;
  let passed: boolean | null = null;
  for (const k of ["passed", "pass", "ok"]) if (typeof o[k] === "boolean") { passed = o[k]; break; }
  if (passed === null && typeof o.status === "string") passed = /pass/i.test(o.status) ? true : /fail/i.test(o.status) ? false : null;
  const findings = (Array.isArray(o.findings) ? o.findings : []).map(findingOf).filter(Boolean) as QaFinding[];
  const counts: Record<FindingTone, number> = { ok: 0, warn: 0, bad: 0, neutral: 0 };
  for (const f of findings) counts[f.tone] += 1;
  const forHash = str(o.forHash ?? o.contentHash);
  const gateHash = gateOf(item)?.contentHash ?? null;
  return {
    passed,
    findings,
    unverified: toLines(o.unverified).map((l) => truncate(l, 400)).slice(0, 25),
    counts,
    by: str(o.by),
    at: str(o.at),
    stale: !!forHash && !!gateHash && forHash !== gateHash,
  };
}

// ── UPDATE items ────────────────────────────────────────────────────────────

export interface UpdateInfo {
  /** The WordPress post id (digits only); null when the item names its target some other way. */
  wpPostId: string | null;
  /** The target when it has no id: a slug or an address. */
  target: string | null;
  baseModifiedGmt: string | null;
  baseSlug: string | null;
  baseDisclaimerCount: number | null;
  /** A supervisor correction of a live post (not a planned refresh): it follows the corrections policy, not the blog one. */
  isCorrection: boolean;
}

function postIdOf(v: unknown): { id: string | null; target: string | null } {
  const s = str(v);
  if (!s) return { id: null, target: null };
  const m = /^(?:wp\s?-?)?(\d+)$/i.exec(s);
  return m ? { id: m[1], target: null } : { id: null, target: s };
}

/**
 * Whether this item rewrites a post that is already live, and what it must leave alone. `sourceContext.update` is the marker;
 * an older item that only carries `updateOf` / `correctionOf` (what Peggy and the supervisor wrote before) is an update too,
 * just without the locks.
 */
export function updateInfoOf(item: StudioItem): UpdateInfo | null {
  const ctx = ctxOf(item);
  if (!ctx) return null;
  const u = ctx.update;
  if (u && typeof u === "object" && !Array.isArray(u)) {
    const o = u as Ctx;
    const { id, target } = postIdOf(o.wpPostId ?? o.wpId ?? o.postId);
    const count = typeof o.baseDisclaimerCount === "number" ? o.baseDisclaimerCount : typeof o.baseDisclaimerCount === "string" && o.baseDisclaimerCount.trim() !== "" ? Number(o.baseDisclaimerCount) : NaN;
    return {
      wpPostId: id,
      target,
      baseModifiedGmt: str(o.baseModifiedGmt),
      baseSlug: str(o.baseSlug),
      baseDisclaimerCount: Number.isFinite(count) ? count : null,
      isCorrection: o.isCorrection === true || str(ctx.correctionOf) !== null,
    };
  }
  const legacy = ctx.updateOf ?? ctx.correctionOf;
  const { id, target } = postIdOf(legacy);
  if (id || target) return { wpPostId: id, target, baseModifiedGmt: null, baseSlug: null, baseDisclaimerCount: null, isCorrection: str(ctx.correctionOf) !== null };
  return null;
}

/** WordPress writes modified_gmt without a zone ("2026-09-18T14:03:22"); it is GMT, so it must not be read as local time. */
export function wpGmtDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const t = s.trim();
  return toDate(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(t) ? `${t.replace(" ", "T")}Z` : t);
}

/** The live post on the owner's site (WordPress redirects /?p=<id> to the permalink). Null when the site or the id is unknown. */
export function wpPostLink(workspace: any, wpPostId: string | null): string | null {
  const base = typeof workspace?.wordpressUrl === "string" ? workspace.wordpressUrl.trim() : "";
  if (!base || !wpPostId) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(base) ? base : `https://${base}`);
    u.search = `?p=${encodeURIComponent(wpPostId)}`;
    u.hash = "";
    return u.toString();
  } catch {
    return null;
  }
}

// ── Sources ledger ──────────────────────────────────────────────────────────

export interface LedgerView {
  text: string;
  /** "ledger" = sourceContext.ledger; "comment" = the <!-- SOURCES --> comment inside the article body. */
  from: "ledger" | "comment";
}

/** The ledger text without its comment wrapper, so it reads the same wherever it came from. */
function unwrapLedger(t: string): string {
  return t.replace(/^\s*<!--\s*SOURCES\b/i, "").replace(/-->\s*$/, "").trim();
}

/** `sourceContext.ledger` first; the ledger comment from the article body only when the item carries no ledger of its own. */
export function ledgerOf(item: StudioItem, commentLedger: string | null | undefined): LedgerView | null {
  const raw = ctxOf(item)?.ledger;
  const own = typeof raw === "string" ? raw : Array.isArray(raw) ? raw.filter((s) => typeof s === "string").join("\n") : "";
  const text = unwrapLedger(own);
  if (text) return { text, from: "ledger" };
  const c = (commentLedger ?? "").trim();
  return c ? { text: c, from: "comment" } : null;
}

export type StageGroup = "pending" | "passed" | "in_force" | "ended" | "other";

export interface LedgerStage {
  group: StageGroup;
  label: string;
}

/** A ledger `stage:` value as a badge: its group drives the colour, its own words (and any date after them) are the label. */
export function ledgerStageOf(raw: string): LedgerStage {
  const v = raw.replace(/[`_|]/g, " ").replace(/\s+/g, " ").trim().slice(0, 60);
  const l = v.toLowerCase();
  let group: StageGroup = "other";
  if (/withdrawn|repealed|died|lapsed|defeated|expired|revoked/.test(l)) group = "ended";
  else if (/\bnot (yet )?in[ -]?force\b|\bnot yet (effective|law)\b/.test(l)) group = /assent|passed|enacted/.test(l) ? "passed" : "pending";
  else if (/\bin[ -]?force\b/.test(l)) group = "in_force";
  else if (/royal assent|\bpassed\b|\benacted\b|\bassented\b/.test(l)) group = "passed";
  else if (/proposed|proposal|first reading|second reading|third reading|committee|introduced|tabled|announced|draft|budget|\bbill\b/.test(l)) group = "pending";
  return { group, label: v ? v[0].toUpperCase() + v.slice(1) : "Unknown" };
}

/** Marker for an inline-code span that the ledger renderer turns into a stage badge. */
export const STAGE_MARK = "§stage:";

export function parseStageMark(code: string): LedgerStage | null {
  if (!code.startsWith(STAGE_MARK)) return null;
  const rest = code.slice(STAGE_MARK.length);
  const i = rest.indexOf("|");
  if (i < 0) return null;
  const group = rest.slice(0, i) as StageGroup;
  return { group: (["pending", "passed", "in_force", "ended", "other"] as string[]).includes(group) ? group : "other", label: rest.slice(i + 1) };
}

const STAGE_LINE = /^(\s*(?:[-*+]\s+)?)stage:\s*(.+?)\s*$/i;
const STAGE_INLINE = /\bstage:\s*(proposed|first reading|royal assent|in force|passed)\b/gi;

/**
 * The ledger text with every `stage:` value replaced by a marker the renderer shows as a badge, plus the stages found.
 * A `- stage: <anything>` line takes the whole value; a `stage: <known stage>` inside a longer line takes just that stage.
 */
export function markLedgerStages(text: string): { text: string; stages: LedgerStage[] } {
  const stages: LedgerStage[] = [];
  const mark = (s: LedgerStage) => `\`${STAGE_MARK}${s.group}|${s.label.replace(/`/g, "'")}\``;
  const out = text.split(/\r?\n/).map((line) => {
    const m = STAGE_LINE.exec(line);
    if (m) {
      const s = ledgerStageOf(m[2]);
      stages.push(s);
      return `${m[1]}stage: ${mark(s)}`;
    }
    return line.replace(STAGE_INLINE, (_all, v: string) => {
      const s = ledgerStageOf(v);
      stages.push(s);
      return `stage: ${mark(s)}`;
    });
  });
  return { text: out.join("\n"), stages };
}

/** Facts whose law has not come into force yet (proposed, in Parliament, or passed but not yet in effect). */
export function stagesNotInForce(stages: LedgerStage[]): number {
  return stages.filter((s) => s.group === "pending" || s.group === "passed").length;
}

// ── Workspace histograms ────────────────────────────────────────────────────

export interface HistogramRow {
  id: string;
  label: string;
  help: string;
  /** How many pieces scored 0, 1, 2 and 3 on this check. */
  counts: [number, number, number, number];
  total: number;
  /** The server's verdict that nearly every recent piece scored 3, so the check no longer tells strong pieces from weak ones. */
  saturated: boolean;
  /** Enough pieces are graded for that verdict to mean something (false = a handful of 3s so far). Assumed true when the server does not say. */
  sampleSufficient: boolean;
}

export interface QualityHistogram {
  /** Published pieces in the period that carry a score card. */
  items: number;
  rows: HistogramRow[];
  /** At least one check has at least one score. */
  hasData: boolean;
  /** The server's rule for "saturated": this share (0 to 1) of at least this many graded pieces scored 3. Null when it does not say. */
  threshold: number | null;
  minSample: number | null;
}

function countOf(x: unknown): number {
  const n = typeof x === "number" ? x : typeof x === "string" && x.trim() !== "" ? Number(x) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function histRowOf(id: string, raw: unknown): HistogramRow {
  const meta = RUBRIC_BY_ID.get(id);
  const o = raw && typeof raw === "object" ? (raw as Ctx) : {};
  const arr = Array.isArray(raw) ? raw : Array.isArray(o.counts) ? o.counts : null;
  const counts: [number, number, number, number] = arr
    ? [countOf(arr[0]), countOf(arr[1]), countOf(arr[2]), countOf(arr[3])]
    : [countOf(o["0"]), countOf(o["1"]), countOf(o["2"]), countOf(o["3"])];
  return {
    id,
    label: meta?.label ?? humanizeCheckName(id),
    help: meta?.help ?? "",
    counts,
    total: counts[0] + counts[1] + counts[2] + counts[3],
    saturated: o.saturated === true,
    sampleSufficient: o.sampleSufficient !== false,
  };
}

/** All ten rubric checks always get a row (an empty one when nothing was scored); checks the server adds follow. */
export function normalizeHistogram(json: unknown): QualityHistogram {
  const j = json && typeof json === "object" ? (json as Ctx) : {};
  const checks = j.checks && typeof j.checks === "object" && !Array.isArray(j.checks) ? (j.checks as Ctx) : {};
  const rows = RUBRIC.map((r) => histRowOf(r.id, checks[r.id]));
  for (const id of Object.keys(checks)) if (!RUBRIC_BY_ID.has(id)) rows.push(histRowOf(id, checks[id]));
  return {
    items: countOf(j.items),
    rows,
    hasData: rows.some((r) => r.total > 0),
    threshold: typeof j.threshold === "number" && j.threshold > 0 && j.threshold <= 1 ? j.threshold : null,
    minSample: countOf(j.minSample) || null,
  };
}

export function useQualityHistogram(days = 30) {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const q = useQuery<{ available: boolean; data: QualityHistogram }>({
    queryKey: ["/api/workspaces", wsId, "quality-histogram", days],
    enabled: !!wsId,
    staleTime: 60_000,
    queryFn: async () => {
      const json = await getOrNull(`/api/workspaces/${wsId}/quality-histogram?days=${days}`);
      return json === null ? { available: false, data: normalizeHistogram(null) } : { available: true, data: normalizeHistogram(json) };
    },
  });
  return {
    data: q.data?.data ?? normalizeHistogram(null),
    available: q.data?.available ?? true,
    isLoading: q.isLoading,
    isError: q.isError && !q.data,
    refetch: q.refetch,
  };
}
